import { useTranslation } from 'react-i18next';
import { useCallback } from 'react';
import BasePageComponent from '../../components/BasePageComponent';
import { useIntersectionObserver } from '../../hooks/useIntersectionObserver';
import './Products.css';

type ProductItem = {
  id: string;
  name: string;
  size: string | string[];
  cut_rate?: string;
  description: string;
  price: string;
  image: string;
};

type ProductCategory = {
  title: string;
  description: string;
  items: ProductItem[];
};

const Products = (): JSX.Element => {
  const { t } = useTranslation('translation', { keyPrefix: 'products' });

  const handleIntersection = useCallback((entry: IntersectionObserverEntry) => {
    if (entry.isIntersecting) {
      entry.target.classList.add('visible');
      
      if (entry.target.classList.contains('category-section')) {
        const cards = entry.target.querySelectorAll('.product-card');
        cards.forEach((card, index) => {
          setTimeout(() => {
            card.classList.add('visible');
          }, index * 100);
        });
      }
    }
  }, []);

  const observerOptions = {
    threshold: 0.2,
    rootMargin: '-50px 0px'
  };

  const categories: { [key: string]: ProductCategory } = {
    original: {
      title: t('original.title'),
      description: t('original.description'),
      items: [
        {
          id: 'keel_xa',
          name: t('original.items.keel_xa.name'),
          size: t('original.items.keel_xa.size'),
          description: t('original.items.keel_xa.description'),
          price: t('original.items.keel_xa.price'),
          image: '/images/scissors/keel_xa.png',
        },
        {
          id: 'keel_za',
          name: t('original.items.keel_za.name'),
          size: t('original.items.keel_za.size'),
          description: t('original.items.keel_za.description'),
          price: t('original.items.keel_za.price'),
          image: '/images/scissors/keel_za.png',
        },
        {
          id: 'calis_xa',
          name: t('original.items.calis_xa.name'),
          size: t('original.items.calis_xa.size'),
          description: t('original.items.calis_xa.description'),
          price: t('original.items.calis_xa.price'),
          image: '/images/scissors/calis_xa.png',
        },
        {
          id: 'calis_za',
          name: t('original.items.calis_za.name'),
          size: t('original.items.calis_za.size'),
          description: t('original.items.calis_za.description'),
          price: t('original.items.calis_za.price'),
          image: '/images/scissors/calis_za.png',
        },
      ],
    },
    thinning: {
      title: t('thinning.title'),
      description: t('thinning.description'),
      items: [
        {
          id: 'zwxa',
          name: t('thinning.items.zwxa.name'),
          size: t('thinning.items.zwxa.size'),
          cut_rate: t('thinning.items.zwxa.cut_rate'),
          description: t('thinning.items.zwxa.description'),
          price: t('thinning.items.zwxa.price'),
          image: '/images/scissors/zwxa.png',
        },
        {
          id: 'hurry_a12',
          name: t('thinning.items.hurry_a12.name'),
          size: t('thinning.items.hurry_a12.size'),
          cut_rate: t('thinning.items.hurry_a12.cut_rate'),
          description: t('thinning.items.hurry_a12.description'),
          price: t('thinning.items.hurry_a12.price'),
          image: '/images/scissors/hurry_a12.png',
        },
        {
          id: 'asa_25',
          name: t('thinning.items.asa_25.name'),
          size: t('thinning.items.asa_25.size'),
          cut_rate: t('thinning.items.asa_25.cut_rate'),
          description: t('thinning.items.asa_25.description'),
          price: t('thinning.items.asa_25.price'),
          image: '/images/scissors/asa_25.png',
        },
      ],
    },
    standard: {
      title: t('standard.title'),
      description: t('standard.description'),
      items: [
        {
          id: 'bcv_60',
          name: t('standard.items.bcv_60.name'),
          size: t('standard.items.bcv_60.size'),
          description: t('standard.items.bcv_60.description'),
          price: t('standard.items.bcv_60.price'),
          image: '/images/scissors/bcv_60.png',
        },
        {
          id: 'zcv',
          name: t('standard.items.zcv.name'),
          size: t('standard.items.zcv.size'),
          description: t('standard.items.zcv.description'),
          price: t('standard.items.zcv.price'),
          image: '/images/scissors/zcv.png',
        },
        {
          id: 'bzc_30',
          name: t('standard.items.bzc_30.name'),
          size: t('standard.items.bzc_30.size'),
          description: t('standard.items.bzc_30.description'),
          price: t('standard.items.bzc_30.price'),
          image: '/images/scissors/bzc_30.png',
        },
      ],
    },
  };

  const renderProductSection = (categoryKey: string, ref: React.RefObject<HTMLElement>) => {
    const category = categories[categoryKey];
    return (
      <section className="category-section" ref={ref} id={categoryKey}>
        <h2 className="category-title">{category.title}</h2>
        <div className="product-card-container">
          {category.items.map((product) => (
            <div key={product.id} className="product-card">          
              <div className="product-info">
                <h3 className="product-name">{product.name}</h3>
                <p className="product-description">
                  {/* {Array.isArray(product.size)
                    ? `サイズ: ${product.size.join(', ')}`
                    : product.size && `サイズ: ${product.size}`}
                  {product.cut_rate && `カット率: ${product.cut_rate}`} */}
                  {product.description}
                </p>
                <p className="product-price">{product.price}</p>
              </div>
              <div className="product-image-container">
                <img
                  className="product-image"
                  src={product.image}
                  alt={product.name}
                />
              </div>
            </div>
          ))}
        </div>
      </section>
    );
  };

  const originalSectionRef = useIntersectionObserver<HTMLElement>(handleIntersection, observerOptions);
  const thinningSectionRef = useIntersectionObserver<HTMLElement>(handleIntersection, observerOptions);
  const standardSectionRef = useIntersectionObserver<HTMLElement>(handleIntersection, observerOptions);

  return (
    <BasePageComponent
      id="products"
      title={t('title')}
      description={t('description')}
    >
      {renderProductSection('original', originalSectionRef)}
      {renderProductSection('thinning', thinningSectionRef)}
      {renderProductSection('standard', standardSectionRef)}
    </BasePageComponent>
  );
};

export default Products; 